import React, { FC } from 'react'
import { NavLink } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { FieldArray } from 'formik'
import { differenceWith } from 'lodash'
import { datadogLogs } from '@datadog/browser-logs'

import { useAttributes } from 'Api'
import { Button as MerinioButton, Card } from 'ui'

import { getAttributeName, getAttributeRankName } from 'services/Utilities'
import { hasAccessToThis } from 'services/Utilities'
import { DateTimePicker } from 'components/Forms'
import Select from 'components/Forms/Select'

import { t } from 'translations'

type AttributesProps = {
  name?: string
  module?: string
  attributes: MData.User['attributes']
  disabled: boolean
  onlySelfAssignable?: boolean
  displayExpirationDateField?: boolean
}

const Attributes: FC<AttributesProps> = ({
  name = 'attributes',
  module = 'administration.positions',
  attributes,
  disabled,
  onlySelfAssignable,
  displayExpirationDateField
}) => {
  const { enableAttributesExpiration, enableAttributes } = useSelector((state: MStore.RootState) => state.settings)
  const attributeManage = hasAccessToThis('attributeManage')
  const { data: attributesData } = useAttributes()

  if (!enableAttributes) return null

  const { attributesById, attributes: allAttributes } = attributesData || {}

  // Creates an array of attributes the user can add
  const availableAttributesToAdd = differenceWith(allAttributes, attributes, (a, b) => a._id === b.attributeId).filter(attribute =>
    onlySelfAssignable ? attribute.selfAssign : true
  )

  return (
    <>
      <h5>{t(`${module}.${name.toUpperCase()}.TITLE`)}</h5>
      <small className="form-text text-muted">{t(`${module}.${name.toUpperCase()}.DESCRIPTION`)}</small>
      {!allAttributes?.length ? (
        <div className="mb-4">
          {attributeManage ? (
            <>
              {t('components.addResource.NO_RESOURCES')}
              <div>
                <NavLink to={'/admin/attributes'}>{t('components.addResource.ADD_RESOURCES')}</NavLink>
              </div>
            </>
          ) : (
            <>
              {t('components.addResource.NO_RESOURCES')} {t('components.addResource.NO_PERMISSION')}
            </>
          )}
        </div>
      ) : (
        <FieldArray
          name={name}
          render={arrayHelpers => (
            <Card className="bg-light mb-3">
              <div className="p-3">
                {attributes?.map((attribute, index) => {
                  const shouldDisplayExpirationDateField =
                    displayExpirationDateField && attributesById?.[attribute.attributeId]?.hasExpirationDate && enableAttributesExpiration
                  if (!attributesById?.[attribute.attributeId]) {
                    datadogLogs.logger.error('Attempted to read self-assign property on nonexistent attribute', {
                      attribute,
                      attributeId: attribute.attributeId
                    })
                  }
                  const isASelfAssignableAttribute = attributesById?.[attribute.attributeId].selfAssign
                  const isNotUsable = disabled || (onlySelfAssignable && !isASelfAssignableAttribute)

                  return (
                    <React.Fragment key={index}>
                      {isNotUsable ? (
                        <div>
                          <span>
                            {getAttributeName(attribute.attributeId)} ({getAttributeRankName(attribute.attributeId, attribute.rank)})
                          </span>
                        </div>
                      ) : (
                        <>
                          <div className="d-md-flex">
                            <div className="flex-grow-1 mr-md-2">
                              <Select
                                module={'administration.positions.form'}
                                name={`${name}[${index}].attributeId`}
                                required
                                translation="attributeName"
                                options={allAttributes.filter(att => (onlySelfAssignable ? att.selfAssign : true))}
                                disabled={isNotUsable}
                              />
                            </div>
                            {attributesById?.[attribute.attributeId]?.type !== 'alert' ? (
                              <div className="flex-grow-1 mr-md-2">
                                <Select
                                  module="administration.positions.form"
                                  name={`${name}[${index}].rank`}
                                  required
                                  translation="rank"
                                  options={attributesById?.[attribute.attributeId].levels.map(({ rank: _id, name }) => ({ _id, name }))}
                                  disabled={isNotUsable}
                                  dontSort
                                />
                              </div>
                            ) : null}
                            {shouldDisplayExpirationDateField ? (
                              <div className="flex-grow-1 mr-md-2">
                                <DateTimePicker
                                  module="manager.users.form"
                                  name={`${name}[${index}].expirationDate`}
                                  translation="expirationDate"
                                  type="date"
                                  required
                                />
                              </div>
                            ) : null}

                            {!isNotUsable && (
                              <div style={{ marginTop: '32px' }}>
                                <MerinioButton type="button" color="secondary" onClick={() => arrayHelpers.remove(index)} outlined>
                                  <i className="fas fa-trash" />
                                </MerinioButton>
                              </div>
                            )}
                          </div>
                        </>
                      )}
                      {index !== attributes.length - 1 ? <hr /> : null}
                    </React.Fragment>
                  )
                })}
                {!disabled && availableAttributesToAdd.length ? (
                  <MerinioButton
                    type="button"
                    color="secondary"
                    className="mt-3"
                    onClick={() => arrayHelpers.push({ attributeId: availableAttributesToAdd[0]._id, rank: availableAttributesToAdd[0].levels[0].rank })}
                    outlined
                  >
                    <i className="fas fa-plus" />
                  </MerinioButton>
                ) : null}
              </div>
            </Card>
          )}
        />
      )}
    </>
  )
}

export default Attributes
